import BannerComp from "../../components/BannerComp";
import FeatureImageContent from "../../components/FeatureImageContent";
import IconBenefitGrid from "../../components/IconBenefitGrid";
import ImageTextSection from "../../components/ImageTextSection";
import NumbersFlow from "../../components/NumbersFlow";
import Seo from "../../components/Seo";
import { benefitsData, collectionSteps } from "../../data/emr";
import { createBreadcrumbSchema, createWebPageSchema } from "../../lib/seo";

import section1Image from "./emr_section1.webp";
import section2Image from "./emr_section2.webp";
import section3Image from "./emr_section3.webp";

const seo = {
  title: "EMR for Healthcare Management | MIT4Doctors",
  description:
    "Managing patient data is tough for healthcare providers. Our simple EMR system provides secure and efficient monthly reports.",
  schema: [
    createWebPageSchema({
      path: "/services/emr-subscription",
      title: "EMR for Healthcare Management",
      description:
        "Managing patient data is tough for healthcare providers. Our simple EMR system provides secure and efficient monthly reports.",
    }),
    createBreadcrumbSchema(
      "/services/emr-subscription",
      "EMR for Healthcare Management",
    ),
  ],
};

export default function EmrSubscription() {
  return (
    <>
      <Seo
        title={seo.title}
        metaTitle={seo.title}
        description={seo.description}
        canonicalPath="/services/emr-subscription"
        schema={seo.schema}
      />
      <BannerComp
        heading="EMR for Healthcare Management"
        subHeading="A Smarter, More Efficient Way to Manage Patient Records"
        paragraph="Managing patient data is tough for healthcare providers. Our simple EMR system provides secure and efficient monthly reports."
        centered
        BannerBgImg="/img/banners/emr_hero.webp"
      />

      <ImageTextSection
        image={section1Image}
        imageAlt="What Makes EMR Significant?"
        imagePosition="left"
        title="What Makes EMR Significant?"
        description="It helps the operation run smoothly, improves patient care, and increases revenue. Furthermore, you will not have the risk of paying high upfront expenses. The advanced EMR tool works well with a clinic's billing, scheduling, and compliance systems. This software, in particular, helps medical professionals focus on patient care."
        highlightWords="EMR"
      />

      <IconBenefitGrid
        title="Why Choose Our EMR Subscription ?"
        description="Our EMR subscription provides secure, efficient, and easy-to-use healthcare management tools that streamline clinical workflows, improve patient records, and support better practice efficiency."
        highlightWords="Subscription"
        items={benefitsData}
      />

      <FeatureImageContent
        heading="Get Started Today"
        description="Cut Costs, Not Quality — Guarantee 15%+ Savings with MITSI EMR! Upgrade your practice with our EMR solution for better patient care, billing, and workflow. Contact us today to learn about our monthly EMR subscription."
        buttonLabel="Get Started"
        buttonUrl="/contact-us"
        imageAlt="Get Started Today"
      />

      <NumbersFlow
        eyebrow="EMR Subscription Features"
        heading="Key Features of Our EMR Subscription"
        highlights={["EMR", "Subscription"]}
        intro="Efficient, Secure, and Scalable EMR Solutions for Every Practice!"
        steps={collectionSteps}
      />

      <ImageTextSection
        image={section2Image}
        imageAlt="No Upfront Costs — Simple Monthly Subscription"
        imagePosition="right"
        title="No Upfront Costs — Simple Monthly Subscription"
        description="We make EMR adoption easy and affordable. With our monthly subscription model, there are:"
        highlightWords="Monthly Subscription"
        subSections={[
          {
            listItems: [
              "No expensive setup fees",
              "No long-term contracts",
              "No hidden costs",
            ],
          },
          {
            description:
              "This pricing model helps healthcare providers use a secure and efficient medical records system. They can enjoy the benefits without worrying about costs.",
          },
        ]}
        icon={true}
      />

      <ImageTextSection
        image={section3Image}
        imageAlt="Who Can Benefit from Our EMR Subscription?"
        imagePosition="left"
        title="Who Can Benefit from Our EMR Subscription?"
        highlightWords="EMR Subscription"
        subSections={[
          {
            heading: "Primary Care Providers",
            listItems: [
              "Ease documentation and patient management.",
              "Improve efficiency with integrated billing and coding solutions.",
              "Reduce administrative workload while enhancing patient engagement.",
            ],
          },
          {
            heading: "Specialists",
            listItems: [
              "Custom templates and workflows designed for specialty practices.",
              "Advanced reporting for tracking patient outcomes.",
              "Secure access to records for faster diagnosis and treatment planning.",
            ],
          },
          {
            heading: "Large Healthcare Facilities",
            listItems: [
              "Multi-provider, multi-location support with seamless data sharing.",
              "Compliance with industry regulations, including HIPAA billing standards.",
              "Scalable solutions for growing healthcare networks.",
            ],
          },
        ]}
        icon={true}
        buttonLabel="Call Now"
        buttonUrl="/contact-us"
      />
    </>
  );
}
